import { Attribute } from "./attribute";
import { Offer } from "./offer";
import { CategoryPicture, LightPicture, Picture } from "./picture";
import { Page } from "@/models/page";
import { Media } from "@/models/media";
import { Video } from "@/models/video";

export class Product {
  id?: string;
  reference?: string;
  slug?: string;
  name?: string;
  description?: string;
  gallery?: Picture[];
  lightGallery?: Media[];
  pictureCover?: LightPicture | "";
  categoryPaths?: string[];
  attributeValuesByAttributeName?: Record<string, Attribute>;
  descriptionShort?: Attribute;
  descriptionLong?: Attribute;
  focus?: FocusMarketing[] | any;
  relatedProduct?: Attribute | undefined;
  children?: Product[];
  configurableAttributes?: any;
  offers?: Offer[];
  percentPromo?: number;
  stockQuantity?: number;
  taxIncludedPrice?: number | null;
  taxFreePrice?: number | null;
  discountPrice?: number | null;
  minimalOrderQuantity?: number;
  maximalOrderQuantity?: number;
  isNew?: Attribute | boolean;
  seoTitle?: Attribute | null;
  seoDescription?: Attribute | null;
  refSocatec?: string | Record<string, Attribute> | undefined;
  defaultOfferId?: string;
  notices?: Notices[];
  brand?: Attribute;
  restock_date?: Attribute;
  ean?: Attribute;
  upCycling?: boolean;
  madeInFrance?: boolean;
  madeInEurope?: boolean;
  limitedEdition?: boolean;
  private defaultOffer?: Offer;

  public static of(dto: any): Product {
    const p: Product = new Product();
    p.id = dto.id;
    p.reference = dto.reference;
    p.slug = dto.slug;
    p.name = dto.name;
    p.categoryPaths = dto.categoryPaths;

    Object.keys(dto.attributeValuesByAttributeName).forEach(function (key, index) {
      dto.attributeValuesByAttributeName[key].index = index;
      dto.attributeValuesByAttributeName[key] = Attribute.of(dto.attributeValuesByAttributeName[key]);
    });

    p.attributeValuesByAttributeName = dto.attributeValuesByAttributeName;
    p.description = dto.description;
    p.configurableAttributes = dto?.configurableAttributes;
    p.defaultOfferId = dto?.defaultOfferId;
    // Set description short & long and delete reference in attributes list
    p.descriptionShort = p.attributeValuesByAttributeName?.["afs_description_short"];
    delete p.attributeValuesByAttributeName?.["afs_description_short"];
    p.descriptionLong = p.attributeValuesByAttributeName?.["afs_description_long"];
    delete p.attributeValuesByAttributeName?.["afs_description_long"];
    p.relatedProduct = p.attributeValuesByAttributeName?.["afs_marketing_associated_products"];
    delete p.attributeValuesByAttributeName?.["afs_marketing_associated_products"];
// remove shipping attribute
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_width"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_height"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_length"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_weight"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_width_b2b"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_height_b2b"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_length_b2b"];
    delete p.attributeValuesByAttributeName?.["afs_logistic_transport_weight_b2b"];
    //remove ean
    p.ean = p.attributeValuesByAttributeName?.["afs_ean"];
    delete p.attributeValuesByAttributeName?.["afs_ean"];
    const upCycling = p.attributeValuesByAttributeName?.["afs_marketing_upcycling"] || false;
    if (upCycling) {
      p.upCycling = upCycling.value === "true";
    }
    delete p.attributeValuesByAttributeName?.["afs_marketing_upcycling"];

    const limitedEdition = p.attributeValuesByAttributeName?.["afs_marketing_limited_edition"] || false;
    if (limitedEdition) {
      p.limitedEdition = limitedEdition.value === "true";
    }
    delete p.attributeValuesByAttributeName?.["afs_marketing_limited_edition"];

    const madeInFrance = p.attributeValuesByAttributeName?.["afs_marketing_made_in_france"] || false;
    if (madeInFrance) {
      p.madeInFrance = madeInFrance.value === "true";
    }
    delete p.attributeValuesByAttributeName?.["afs_marketing_made_in_france"];

    const madeInEurope = p.attributeValuesByAttributeName?.["afs_marketing_made_in_europe"] || false;
    if (madeInEurope) {
      p.madeInEurope = madeInEurope.value === "true";
    }
    delete p.attributeValuesByAttributeName?.["afs_marketing_made_in_europe"];

    const isNew = p.attributeValuesByAttributeName?.["afs_marketing_new"] || false;
    if (isNew) {
      p.isNew = isNew.value === "true";
    }

    delete p.attributeValuesByAttributeName?.["afs_marketing_new"];

    p.seoTitle = p.attributeValuesByAttributeName?.["afs_marketing_seo_title"] || null;
    delete p.attributeValuesByAttributeName?.["afs_marketing_seo_title"];
    p.seoDescription = p.attributeValuesByAttributeName?.["afs_marketing_seo_description"] || null;
    delete p.attributeValuesByAttributeName?.["afs_marketing_seo_description"];
    delete p.attributeValuesByAttributeName?.["afs_marketing_new"];
    p.refSocatec = p.attributeValuesByAttributeName?.["afs_ref_soc7"] ? p.attributeValuesByAttributeName?.["afs_ref_soc7"].value : p.reference;
    delete p.attributeValuesByAttributeName?.["afs_ref_soc7"];
    p.brand = p.attributeValuesByAttributeName?.["afs_details_brand"];
    p.restock_date = p.attributeValuesByAttributeName?.["afs_supply_restock_date"];
    delete p.attributeValuesByAttributeName?.["afs_supply_restock_date"];
    p.notices = [];
    if (dto?.attributeValuesByAttributeName?.["afs_docs"]) {
      const tempFocus: any[] = JSON.parse(dto?.attributeValuesByAttributeName?.["afs_docs"]?.value);
      if (tempFocus) {

        tempFocus.forEach(b => p.notices?.push(Notices.of(b)));
      }
      delete p.attributeValuesByAttributeName?.["afs_docs"];
    }
    p.focus = [];
    p.offers = [];

    if (dto?.attributeValuesByAttributeName?.["afs_marketing_focus"]) {
      const tempFocus: any[] = JSON.parse(dto?.attributeValuesByAttributeName?.["afs_marketing_focus"]?.value);
      if (tempFocus) {
        tempFocus.forEach(b => p.focus.push(FocusMarketing.of(b)));
      }
      delete p.attributeValuesByAttributeName?.["afs_marketing_focus"];
    }

    let productLightGallery = [];
    if (dto?.pictures?.length > 0) {
      p.gallery = dto.pictures.map(p => {
        return Picture.of(p, dto.name);
      });
      productLightGallery = productLightGallery.concat(
        dto.pictures.map(p => {
          return LightPicture.of(p, dto.name);
        })
      );

      const cover = dto.pictures.find(p => p.isCover === true);
      p.pictureCover = cover && LightPicture.of(cover, dto.name);
    }

    if (dto?.videos?.length > 0) {
      productLightGallery = productLightGallery.concat(dto.videos.map(v => {
        return Video.of(v, dto.name);
      }));
    }

    p.lightGallery = productLightGallery;

    if (dto.children && dto.children.length > 0) {
      p.children = [];
      dto.children.forEach((child: Product) => {
        if (p.children) {
          p.children.push(Product.of(child));
        }
        if (!p.defaultOffer) {
          p.defaultOffer = child?.offers?.find(o => o.id === dto?.defaultOfferId);
        }

        if (child.attributeValuesByAttributeName?.["afs_marketing_made_in_france"]?.value === "true") {
          p.madeInFrance = true;
        }

      });
    } else if (dto.offers && dto.offers.length > 0) {

      dto.offers.forEach((offer: Offer) => {
        p.offers?.push(Offer.of(offer));
      });
      if (dto?.defaultOfferId && !p.defaultOffer) {
        p.defaultOffer = Offer.of(dto.offers.find(o => o.id === dto?.defaultOfferId));
      }
    }

    p.stockQuantity = p.defaultOffer?.stockQuantity || 0;
    p.taxIncludedPrice = p.defaultOffer?.price?.taxIncluded;
    p.taxFreePrice = p.defaultOffer?.price?.taxFree;
    p.discountPrice = p.defaultOffer?.discountPrice?.taxIncluded;
    if (p.discountPrice && p.taxIncludedPrice) {
      p.percentPromo = ((p.discountPrice - p.taxIncludedPrice) / p.taxIncludedPrice);
    }
    p.minimalOrderQuantity = p.defaultOffer?.minimalOrderQuantity;
    p.maximalOrderQuantity = p.defaultOffer?.maximalOrderQuantity;

    return p;
  }
}

export class FocusMarketing {
  title?: { [key: string]: string };
  description?: { [key: string]: string };
  image?: any;

  static of(data: any) {
    const f: FocusMarketing = new FocusMarketing();

    f.description = data.afs_product_marketing_focus_description || "";
    f.title = data.afs_product_marketing_focus_title || "";
    f.image = data.afs_product_marketing_focus_image ? {
      src: data.afs_product_marketing_focus_image[0]?.url || "",
      alt: data.afs_product_marketing_focus_image[0]?.alt || "",
    } : null;

    return f;
  }
}

export class Notices {
  name?: string;
  url?: string;

  static of(data: any) {
    const n: Notices = new Notices();

    n.name = data.name;
    n.url = data.url;

    return n;
  }
}

export class LightProduct {
  defaultOfferId?: string;
  percentPromo?: number;
  defaultChildSlug?: string;
  price?: number;
  discountPrice?: number;
  stockQuantity?: number;
  reference?: string;
  slug?: string;
  brand?: string;
  name?: string;
  description?: string;
  pictureCover?: CategoryPicture;
  categoryPaths?: string[];
  isConfigurable?: boolean;
  upCycling?: boolean;
  madeInFrance?: boolean;
  madeInEurope?: boolean;
  limitedEdition?: boolean;
  isNew?: boolean;
  attributeValuesByAttributeName?: Record<string, Attribute>;

  public static of(data: any): LightProduct {
    const l: LightProduct = new LightProduct();
    l.defaultOfferId = data?.defaultOfferId;
    l.defaultChildSlug = data?.defaultChildSlug;
    Object.keys(data.attributeValuesByAttributeName).forEach(function (key, index) {
      data.attributeValuesByAttributeName[key].index = index;
      data.attributeValuesByAttributeName[key] = Attribute.of(data.attributeValuesByAttributeName[key]);
    });

    l.attributeValuesByAttributeName = data.attributeValuesByAttributeName;
    l.price = data?.price;
    l.brand = data?.brand;
    l.discountPrice = data?.discountPrice || null;
    l.stockQuantity = data?.stockQuantity || null;
    l.reference = data?.reference;
    l.slug = data?.slug;
    l.name = data?.name;
    if (data?.discountPrice) {
      l.percentPromo = ((data?.discountPrice - data?.price) / data?.price);
    }
    l.description = data?.description;
    l.categoryPaths = data?.categoryPaths;
    l.isConfigurable = data?.isConfigurable;
    l.pictureCover = data?.pictures.filter(picture => (picture.isCover === true)).map(picture => picture ? CategoryPicture.of(picture, data?.name) : CategoryPicture.of([], data?.name))[0] || "";

    const upCycling = l?.attributeValuesByAttributeName?.["afs_marketing_upcycling"] || false;
    if (upCycling) {
      l.upCycling = upCycling.value === "true";
    }

    const limitedEdition = l?.attributeValuesByAttributeName?.["afs_marketing_limited_edition"] || false;
    if (limitedEdition) {
      l.limitedEdition = limitedEdition.value === "true";
    }

    const madeInFrance = l?.attributeValuesByAttributeName?.["afs_marketing_made_in_france"] || false;
    if (madeInFrance) {
      l.madeInFrance = madeInFrance.value === "true";
    }

    const madeInEurope = l?.attributeValuesByAttributeName?.["afs_marketing_made_in_europe"] || false;
    if (madeInEurope) {
      l.madeInEurope = madeInEurope.value === "true";
    }

    const isNew = l?.attributeValuesByAttributeName?.["afs_marketing_new"] || false;
    if (isNew) {
      l.isNew = isNew.value === "true";
    }

    return l;
  }
}

export class ProductsWithFilters {
  products?: Page<LightProduct>;
  filterableAttributes?: unknown;
  currentFilterableAttributes?: unknown;
}
